// RootLayout — providers that need to be INSIDE RouterProvider.
//
// ThemeProvider needs route hooks (useOptionalProjectParams) so it must
// mount inside the router. ToastProvider mounts here too so toast emitters
// from any route can reach the bottom-right stack. AppSideEffects
// (useLanguageSync, navigate wiring, auth-broadcast listener, the tab's one
// live client) lives here as a non-rendering child.

import { Outlet, ScrollRestoration, useNavigate, useHref } from 'react-router-dom';
import { scrollScopeKey } from '../../lib/scrollScope.js';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v7';
import { RouterProvider as AriaRouterProvider, I18nProvider as AriaI18nProvider } from 'react-aria-components';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { ThemeProvider } from '../theme/ThemeProvider.js';
import { ToastProvider } from '../ui/Toast.js';
import { RouteProgress } from './RouteProgress.js';
import { useBusyToast } from '../../hooks/useBusyToast.js';
import { useLanguageSync } from '../../hooks/useLanguageSync.js';
import { setNavigate } from '../../lib/apiClient.js';
import { subscribeAuth } from '../../lib/authBroadcast.js';
import { ME_QUERY_KEY, useMe } from '../../hooks/useMe.js';
import { useOptionalProjectParams } from '../../hooks/useProjectParams.js';
import { liveClient } from '../../lib/live/live.js';
import { consumePendingRemember, useRecentAccountsStore } from '../../stores/recentAccountsStore.js';

function AppSideEffects() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: me } = useMe();
  const projectId = useOptionalProjectParams();
  useLanguageSync();
  // The busy answer's one toast (ADR-079 §5) — the slot filled where the words and the store are.
  useBusyToast();

  // The tab's ONE live client (ADR-074 §1) runs wherever a project is on the URL
  // — HERE, above every frame, because a wizard lives in another frame than the
  // pages and its screen is just as live. It stops on a project-less page and
  // restarts on a project switch; it waits for a signed-in person, so its first
  // glance never races the route guard's own way to the login page.
  const signedIn = me !== undefined;
  useEffect(() => {
    if (!projectId || !signedIn) return;
    liveClient.start(projectId);
    return () => liveClient.stop();
  }, [projectId, signedIn]);

  // Login-convenience: commit a pending "remember this account" intent
  // once the user is authenticated. Single post-auth point for ALL paths —
  // password, MFA, and the full-page SSO callback all land here with `me` set.
  // `consumePendingRemember` is one-shot, so re-runs are no-ops.
  useEffect(() => {
    if (!me) return;
    const pending = consumePendingRemember();
    if (pending) useRecentAccountsStore.getState().remember(pending);
  }, [me]);
  // Wire apiClient interceptor redirects through React Router (so we don't
  // do hard window.location.href navigations for 401/403/404 paths).
  useEffect(() => {
    setNavigate((to, opts) => {
      navigate(to, { replace: opts?.replace, state: opts?.state });
    });
    return () => setNavigate(null);
  }, [navigate]);
  // Layer 3 — cross-tab auth fan-out per ADR-003 §Revocation propagation.
  // One subscription at boot; spec suppresses same-tab echo so the
  // emitter never re-processes its own message.
  useEffect(() => {
    const unsubscribe = subscribeAuth((message) => {
      switch (message.type) {
        case 'logout':
          queryClient.clear();
          if (!window.location.pathname.startsWith('/login')) {
            navigate('/login', { replace: true });
          }
          return;
        case 'login':
          // Actor identity changed — cold-start the new user's bootstrap
          // (i18n language, theme brand, route reset). queryClient.clear()
          // before reload so an unintended hydration cannot reuse stale
          // entries during the unload window.
          queryClient.clear();
          window.location.reload();
          return;
        case 'me-invalidated':
          void queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
          return;
      }
    });
    return unsubscribe;
  }, [navigate, queryClient]);
  return null;
}

export function RootLayout() {
  // React Aria RouterProvider — makes RAC link-items (Menu items with href,
  // future Breadcrumbs, etc.) navigate through React Router (client-side, with
  // modifier-click support) instead of full-page loads. Wired once here, inside
  // React Router's own provider, so useNavigate/useHref are available (ADR-029).
  const navigate = useNavigate();
  // React Aria derives interaction direction (RTL) from ITS OWN locale context,
  // not <html dir>. Without this, React Aria overlays (Menu/Popover/Tooltip/
  // Modal/SlideOver) stay LTR even when the page flips to Arabic. useTranslation
  // re-renders on languageChanged so the locale stays current. (ADR-029)
  const { i18n } = useTranslation();
  return (
    <NuqsAdapter>
    <AriaI18nProvider locale={i18n.language}>
    <AriaRouterProvider navigate={navigate} useHref={useHref}>
      <ThemeProvider>
        <ToastProvider>
          <AppSideEffects />
          {/* Lazy-route / data-load pending strip across the top of the viewport. */}
          <RouteProgress />
          {/* Reset scroll to top on new navigations; restore on back/forward —
              keyed by the ENTITY SCOPE, so a sibling-tab switch on an entity page
              keeps its scroll (lib/scrollScope; FRONTEND-PATTERNS §Scroll doctrine). */}
          <ScrollRestoration getKey={scrollScopeKey} />
          <Outlet />
        </ToastProvider>
      </ThemeProvider>
    </AriaRouterProvider>
    </AriaI18nProvider>
    </NuqsAdapter>
  );
}
